import { Dialog, Field, Icon, Input, Link, RadioGroup, Text, useDisclosure } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { LuArrowRight } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import { toaster } from "../components/ui/toaster";
import { createCardToken } from "../fincode";
import { Plan, useCreateUserAndPaymentMutation } from "../graphql/generated/apollo";
import CreditCardForm from "./CreditCardForm";
import { Button } from "./ui/button";

type Props = {
  trialUser: boolean;
  onlyEnterprisePlan: boolean;
  enterprisePlanName?: string;
  serviceFeePercentage?: number;
  planDescription?: string;
};
export default function ApplyForm({
  trialUser,
  onlyEnterprisePlan,
  enterprisePlanName,
  serviceFeePercentage,
  planDescription,
}: Props) {
  const defaultPlan = onlyEnterprisePlan ? "ENTERPRISE_PLAN" : "STARTER_PLAN";
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    setValue,
    reset,
    watch,
  } = useForm<ApplyFormType>({
    defaultValues: {
      plan: defaultPlan,
    },
  });

  // 選択しているプランをリアルタイム取得
  const watchPlan = watch("plan");

  const navigate = useNavigate();
  const { open, onOpen, onClose } = useDisclosure();

  const [createUserAndPaymentMutation] = useCreateUserAndPaymentMutation();

  const serviceFeePercentageText = serviceFeePercentage !== undefined ? serviceFeePercentage.toString() : "2";

  const enterprisePlanNameText = enterprisePlanName !== undefined ? enterprisePlanName : "エンタープライズ";

  // 選択されたプランに応じて月額料金＋サービス利用料を表示する関数
  const getPlanPricingLabel = (plan: PlanType) => {
    switch (plan) {
      case "STARTER_PLAN":
        // trialUser (初月無料) は表示するかどうか判定
        return `スタータープラン：月額 980円 + サービス利用料 7% ${trialUser ? "（初月無料）" : ""}`;
      case "GROWTH_PLAN":
        return `グロースプラン：月額 4,980円 + サービス利用料 3% ${trialUser ? "（初月無料）" : ""}`;
      case "ENTERPRISE_PLAN":
        return `${enterprisePlanNameText}プラン：月額 14,980円 + サービス利用料 ${serviceFeePercentageText}% ${
          trialUser ? "（初月無料）" : ""
        }`;
      default:
        return "";
    }
  };

  // フォーム送信時のハンドラー
  async function onSubmit(values: ApplyFormType) {
    try {
      const card = {
        card_no: values.cardNumber.toString(),
        expire: formatDate(values.expirationMonth, values.expirationYear),
        holder_name: values.billingName,
        security_code: values.securityCode.toString(),
        number: "1",
      };

      const res = await createCardToken(card);
      const resData = await res.json();

      if (res.status !== 200) {
        throw new Error("クレジットカードが無効です。入力内容が正しいかお確かめください。");
      }
      const token = resData.list[0].token as string;

      let plan: Plan;
      if (values.plan === "STARTER_PLAN") {
        plan = Plan.Starter;
      } else if (values.plan === "GROWTH_PLAN") {
        plan = Plan.Growth;
      } else if (values.plan === "ENTERPRISE_PLAN") {
        plan = Plan.Enterprise;
      } else {
        throw new Error("プランが選択されていません");
      }

      try {
        const result = await createUserAndPaymentMutation({
          variables: {
            input: {
              token: token,
              plan: plan,
              shopName: values.shopName,
              ownerName: values.ownerName,
              linkUrl: values.linkUrl,
              mailAddress: values.mailAddress,
              phoneNumber: values.phoneNumber,
              password: values.password,
              trialUser: trialUser,
            },
          },
        });
        if (result.errors !== undefined) {
          reset();
          onOpen();
          throw new Error("createUserAndPaymentMutation失敗");
        }
      } catch {
        reset();
        onOpen();
        throw new Error("createUserAndPaymentMutation失敗");
      }

      toaster.success({
        title: "申請が完了しました。",
      });
      reset();
      navigate("/");
    } catch (error) {
      if (error instanceof Error && error?.message !== "createUserAndPaymentMutation失敗") {
        toaster.error({
          title: error instanceof Error ? error.message : "An unknown error occurred",
        });
      }
    }
  }

  return (
    <>
      <form>
        <Field.Root mb="8">
          <Field.Label borderRadius="md">
            <Text fontSize="sm">
              ショップ名{" "}
              <Text as="span" color="#f23a3c">
                *
              </Text>
            </Text>
          </Field.Label>
          <Input
            marginTop="0px"
            borderRadius="md"
            borderColor="gray.300"
            placeholder="Easy Vintage"
            {...register("shopName", {
              required: "この項目は必須です",
            })}
          />
          <Text color="#f23a3c" textAlign="left" fontSize="small">
            {errors.shopName?.message}
          </Text>
        </Field.Root>
        <Field.Root mb="8">
          <Field.Label borderRadius="md">
            <Text fontSize="sm">
              ご担当者さま氏名{" "}
              <Text as="span" color="#f23a3c">
                *
              </Text>
            </Text>
          </Field.Label>
          <Input
            marginTop="0px"
            borderRadius="md"
            borderColor="gray.300"
            placeholder="楽出 太郎"
            {...register("ownerName", {
              required: "この項目は必須です",
            })}
          />
          <Text color="#f23a3c" textAlign="left" fontSize="small">
            {errors.ownerName?.message}
          </Text>
        </Field.Root>
        <Field.Root mb="8">
          <Field.Label borderRadius="md">
            <Text fontSize="sm">
              メールアドレス{" "}
              <Text as="span" color="#f23a3c">
                *
              </Text>
            </Text>
          </Field.Label>
          <Input
            type="email"
            marginTop="0px"
            borderRadius="md"
            borderColor="gray.300"
            placeholder="taro.takude@easyvintage.com"
            {...register("mailAddress", {
              required: "この項目は必須です",
            })}
          />
          <Text color="#f23a3c" textAlign="left" fontSize="small">
            {errors.mailAddress?.message}
          </Text>
        </Field.Root>
        <Field.Root mb="8">
          <Field.Label borderRadius="md">
            <Text fontSize="sm" marginBottom="8px">
              パスワード{" "}
              <Text as="span" color="#f23a3c">
                *
              </Text>
            </Text>
            <Text fontSize="xs" color="#444">
              半角英字、数字、記号が使用できます。6文字以上30文字以下で入力してください。
            </Text>
          </Field.Label>
          <Input
            type="password"
            marginTop="0px"
            borderRadius="md"
            borderColor="gray.300"
            {...register("password", {
              required: "この項目は必須です",
              minLength: {
                value: 6,
                message: "パスワードは6文字以上で入力してください",
              },
              maxLength: {
                value: 30,
                message: "パスワードは30文字以下で入力してください",
              },
              pattern: {
                value: /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z\d!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]*$/,
                message: "パスワードは英字と数字を含めるようにしてください",
              },
            })}
          />
          <Text color="#f23a3c" textAlign="left" fontSize="small">
            {errors.password?.message}
          </Text>
        </Field.Root>
        <Field.Root mb="8">
          <Field.Label borderRadius="md">
            <Text fontSize="sm">
              電話番号{" "}
              <Text as="span" color="#f23a3c">
                *
              </Text>
            </Text>
          </Field.Label>
          <Input
            type="tel"
            marginTop="0px"
            borderRadius="md"
            borderColor="gray.300"
            placeholder="090-1234-5678"
            {...register("phoneNumber", {
              required: "この項目は必須です",
            })}
          />
          <Text color="#f23a3c" textAlign="left" fontSize="small">
            {errors.phoneNumber?.message}
          </Text>
        </Field.Root>
        <Field.Root mb="8">
          <Field.Label borderRadius="md">
            <Text fontSize="sm">
              ショップのInstagram/ホームページ{" "}
              <Text as="span" color="#f23a3c">
                *
              </Text>
            </Text>
          </Field.Label>
          <Input
            type="url"
            marginTop="0px"
            borderRadius="md"
            borderColor="gray.300"
            placeholder="https://www.instagram.com/easy_vintage"
            {...register("linkUrl", {
              required: "この項目は必須です",
            })}
          />
          <Text color="#f23a3c" textAlign="left" fontSize="small">
            {errors.linkUrl?.message}
          </Text>
        </Field.Root>
        <Field.Root mb="8">
          <Field.Label borderRadius="md">
            <Text fontSize="sm">
              申し込みプラン（税別）{" "}
              <Text as="span" color="#f23a3c">
                *
              </Text>
            </Text>
          </Field.Label>
          <RadioGroup.Root
            onValueChange={(details) => setValue("plan", details.value as PlanType)}
            defaultValue={defaultPlan}
            marginTop="12px"
          >
            {onlyEnterprisePlan ? (
              <>
                <RadioGroup.Item fontSize="sm" value="ENTERPRISE_PLAN">
                  <RadioGroup.ItemHiddenInput {...register("plan", { required: "この項目は必須です" })} />
                  <RadioGroup.ItemIndicator />
                  <RadioGroup.ItemText>
                    {`${enterprisePlanNameText}プラン（月額14,980円+サービス利用料${serviceFeePercentageText}%${
                      trialUser ? "、ご契約初月無料" : ""
                    }）`}
                  </RadioGroup.ItemText>
                </RadioGroup.Item>
                <Text fontSize="sm" color="#777777">
                  {planDescription}
                </Text>
              </>
            ) : (
              <>
                <RadioGroup.Item fontSize="sm" marginBottom="4px" value="STARTER_PLAN">
                  <RadioGroup.ItemHiddenInput {...register("plan", { required: "この項目は必須です" })} />
                  <RadioGroup.ItemIndicator />
                  <RadioGroup.ItemText>
                    {`スタータープラン（月額980円+サービス利用料7%${trialUser ? "、ご契約初月無料" : ""}）`}
                  </RadioGroup.ItemText>
                </RadioGroup.Item>
                <RadioGroup.Item fontSize="sm" marginBottom="4px" value="GROWTH_PLAN">
                  <RadioGroup.ItemHiddenInput {...register("plan", { required: "この項目は必須です" })} />
                  <RadioGroup.ItemIndicator />
                  <RadioGroup.ItemText>
                    {`グロースプラン（月額4,980円+サービス利用料3%${trialUser ? "、ご契約初月無料" : ""}）`}
                  </RadioGroup.ItemText>
                </RadioGroup.Item>
              </>
            )}
          </RadioGroup.Root>
          <Text color="#f23a3c" textAlign="left" fontSize="small">
            {errors.plan?.message}
          </Text>
        </Field.Root>
        <CreditCardForm register={register} errors={errors} />
      </form>
      {/*
        1) 選択中のプラン価格を申し込みボタンの「上」に表示する
           （getPlanPricingLabel でプランごとに月額と利用料を切り替え）
      */}
      {/* <Text fontSize="md" textAlign="left" marginTop="48px" marginBottom="16px" fontWeight="bold">
        申し込み金額（税別）:
      </Text> */}
      <Text as="h2" fontSize="large" marginBottom="24px" marginTop="108px" fontWeight="bold">
        申し込み金額（税別）
      </Text>
      <Text fontSize="sm" textAlign="left" marginBottom="24px">
        {getPlanPricingLabel(watchPlan)}
      </Text>

      {/* 2) 申込みボタン本体 */}
      <Button
        onClick={handleSubmit(onSubmit)}
        loading={isSubmitting}
        disabled={isSubmitting}
        bg="#333"
        w="100%"
        padding="6"
      >
        <Text color="#fff" fontSize="large">
          ラクダスの利用申込をする <Icon as={LuArrowRight} w={5} h={5} />
        </Text>
      </Button>

      {/* 3) 「利用規約・プライバシーポリシーに同意～」 の文言をボタンの下へ移動 */}
      <Text fontSize="sm" textAlign="left" marginTop="16px" marginBottom="12px">
        <Link href="https://rakudasu.studio.site/tos" target="_blank">
          利用規約
        </Link>
        ・
        <Link href="https://rakudasu.studio.site/privacy" target="_blank">
          プライバシーポリシー
        </Link>
        に同意の上、お申し込みください（
        <Link href="https://rakudasu.studio.site/term" target="_blank">
          特商法に基づく表記
        </Link>
        ）
      </Text>

      <Dialog.Root open={open} onOpenChange={(e) => (e.open ? onOpen() : onClose())}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>申請に失敗しました</Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body>
              <Text mb={4}>現在、問題を解決するために対応中です。解決次第、速やかにご連絡いたします。</Text>
              <Text mb={4}>ご不便をおかけして申し訳ありませんが、少々お待ちください。</Text>
              <Text>何かご不明点やご質問がございましたら、以下のメールアドレスまでお問い合わせください：</Text>
              <Text fontWeight="bold">dev+inquiry@rakudasu.com</Text>
            </Dialog.Body>
            <Dialog.Footer>
              <Button colorPalette="blue" mr={3} onClick={onClose}>
                Close
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </>
  );
}

export type ApplyFormType = {
  shopName: string;
  ownerName: string;
  mailAddress: string;
  password: string;
  phoneNumber: string;
  linkUrl: string;
  plan: PlanType;
  trialUser: boolean;
} & CreditCardFormType;

type PlanType = "STARTER_PLAN" | "GROWTH_PLAN" | "ENTERPRISE_PLAN";
export type CreditCardFormType = {
  cardNumber: number;
  expirationYear: string;
  expirationMonth: string;
  billingName: string;
  securityCode: number;
};

function formatDate(month: string, year: string): string {
  // 年の最後の2桁を取得
  const yearLastTwo = year.slice(-2);
  // 月が1桁の場合に0を追加
  const formattedMonth = month.padStart(2, "0");
  return yearLastTwo + formattedMonth;
}
